import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, ArrowLeft, Infinity as InfinityIcon } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F8F7] text-[#212121] px-6">
      <div className="w-full max-w-md k-card p-10">
        <div className="flex items-center gap-2 mb-8">
          <InfinityIcon className="h-7 w-7 text-[#156F5B]" strokeWidth={2.5} />
          <span className="text-2xl font-bold font-heading text-[#156F5B]">KOIOC</span>
        </div>
        <div className="k-label flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" /> Account recovery
        </div>
        <h1 className="mt-3 text-3xl font-bold font-display text-[#212121]">Set new password</h1>

        {!token ? (
          <div className="mt-8 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700" data-testid="reset-no-token">
            Missing reset token. Use the link from your email, or request a new one.
          </div>
        ) : done ? (
          <div className="mt-8 rounded-md border border-[#B9D6CE] bg-[#F3F8F7] px-5 py-4 text-sm text-[#212121]" data-testid="reset-success">
            Password updated. You can now sign in with your new password.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="reset-form">
            <div>
              <label className="k-sublabel">New password</label>
              <input
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
                className="k-input mt-2"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="k-sublabel">Confirm password</label>
              <input
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input"
                className="k-input mt-2"
                placeholder="••••••••"
              />
            </div>
            {err && (
              <div className="text-sm text-red-700 rounded-md border border-red-200 bg-red-50 px-4 py-2" data-testid="reset-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={busy} data-testid="reset-submit-button" className="k-btn">
              {busy ? "Saving…" : "Update password"}
            </button>
          </form>
        )}

        <div className="mt-8 text-xs">
          <Link to="/login" className="inline-flex items-center gap-1 text-[#156F5B] font-semibold hover:underline" data-testid="back-to-login-link">
            <ArrowLeft className="h-3 w-3" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
